import { useCallback, useEffect, useState, useMemo } from 'react';
import { useDashboard } from '../../../context/dashboardContext';
import { LoadingModal } from '../../../components/shareable/loadingModal';
import toast from 'react-hot-toast';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Mail, Calendar, User, FileText, Clock, ChevronDown, ChevronUp } from 'lucide-react';

const PERIOD_OPTIONS = [
  { label: 'Today', value: 'day' },
  { label: 'This Week', value: 'week' },
  { label: 'This Month', value: 'month' },
  { label: 'This Quarter', value: 'quarter' },
  { label: 'This Year', value: 'year' },
];

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const Reports = () => {
  const { getEmailEvents } = useDashboard();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [expandedRow, setExpandedRow] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'sent_at', direction: 'desc' });

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getEmailEvents(period);
      if (res?.success) {
        setEvents(res.data || []);
      } else {
        toast.error(res?.error || 'Failed to load email events');
      }
    } catch (err) {
      toast.error('Error loading report data');
    } finally {
      setLoading(false);
    }
  }, [getEmailEvents, period]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Build chart data - group events by date bucket
  const chartData = useMemo(() => {
    if (!events.length) return [];

    const buckets = {};

    events.forEach(ev => {
      const d = new Date(ev.sent_at);
      let key;
      if (period === 'day') {
        key = d.toLocaleTimeString('en-US', { hour: 'numeric' });
      } else if (period === 'week') {
        key = d.toLocaleDateString('en-US', { weekday: 'short' });
      } else if (period === 'month') {
        key = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      } else if (period === 'quarter') {
        key = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      } else {
        key = d.toLocaleDateString('en-US', { month: 'short' });
      }

      buckets[key] = (buckets[key] || 0) + 1;
    });

    return Object.entries(buckets).map(([name, count]) => ({ name, count }));
  }, [events, period]);

  // Sorting
  const sortedEvents = useMemo(() => {
    const sorted = [...events];
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        if (sortConfig.key === 'sent_at') {
          aVal = new Date(aVal).getTime();
          bVal = new Date(bVal).getTime();
        }
        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
        if (typeof bVal === 'string') bVal = bVal.toLowerCase();
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sorted;
  }, [events, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === 'asc' ? <ChevronUp className="w-3 h-3 inline ml-1" /> : <ChevronDown className="w-3 h-3 inline ml-1" />;
  };

  return (
    <div className="p-4 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Sales Report</h1>
      </div>

      {/* Quotes Sent Chart Section */}
      <div className="bg-white rounded-xl shadow border border-gray-200 p-5">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-red-600" />
            <h2 className="text-lg font-semibold text-gray-700">Quotes Sent</h2>
            <span className="bg-red-100 text-red-700 text-sm font-medium px-2.5 py-0.5 rounded-full">
              {events.length} total
            </span>
          </div>
          <div className="flex gap-1">
            {PERIOD_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setPeriod(opt.value)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  period === opt.value
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="h-64 flex items-center justify-center">
            <LoadingModal />
          </div>
        ) : chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip
                contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                formatter={(value) => [`${value} quote(s)`, 'Sent']}
              />
              <Bar dataKey="count" fill="#dc2626" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-400">
            No quotes sent in this period
          </div>
        )}
      </div>

      {/* Events Table */}
      <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Email Send History</h2>
        </div>

        {loading ? (
          <div className="p-8 flex justify-center"><LoadingModal /></div>
        ) : sortedEvents.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <Mail className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>No email events found for this period</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700" onClick={() => handleSort('sent_at')}>
                    <Clock className="w-3.5 h-3.5 inline mr-1" />Date Sent<SortIcon columnKey="sent_at" />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700" onClick={() => handleSort('quote_number')}>
                    <FileText className="w-3.5 h-3.5 inline mr-1" />Quote #<SortIcon columnKey="quote_number" />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700" onClick={() => handleSort('sent_by')}>
                    <User className="w-3.5 h-3.5 inline mr-1" />Sent By<SortIcon columnKey="sent_by" />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700" onClick={() => handleSort('customer_name')}>
                    Customer<SortIcon columnKey="customer_name" />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    <Mail className="w-3.5 h-3.5 inline mr-1" />Recipients
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedEvents.map((ev, idx) => (
                  <tr
                    key={ev._id || idx}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => setExpandedRow(expandedRow === idx ? null : idx)}
                  >
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{formatDate(ev.sent_at)}</td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-red-600 font-medium">{ev.quote_number}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{ev.sent_by || '-'}</td>
                    <td className="px-4 py-3 text-gray-700">{ev.customer_name || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {Array.isArray(ev.recipients) ? ev.recipients.join(', ') : ev.recipients || '-'}
                      {/* Review copies (see QUOTE_EMAIL_BCC in the API's routes/quotes.js).
                          Shown here so the copy stays tracked in the report, but kept off the
                          compose/send screen. Tagged BCC so it reads as distinct from the
                          recipients the customer actually saw. */}
                      {Array.isArray(ev.bcc_recipients) && ev.bcc_recipients.length > 0 && (
                        <span className="text-gray-400"> · BCC: {ev.bcc_recipients.join(', ')}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500 truncate max-w-[200px]">{ev.subject || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
